import axios, { AxiosError } from 'axios';
import { Sutra, Project } from '../types/sutra';
import config from '../config';

// Create axios instance with default config
export const apiClient = axios.create({
    baseURL: config.apiUrl,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests if it exists
apiClient.interceptors.request.use((config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle response errors
apiClient.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            // Handle unauthorized access
            localStorage.removeItem('auth_token');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

export const sutraApi = {
    // Auth endpoints
    login: async (username: string, password: string) => {
        const response = await apiClient.post('/auth/login', {
            username: username,
            password: password
        }, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        });
        // console.log('login response:', response.data, username, password, config.apiUrl); 
        const token = response.data.access_token;
        if (token) {
            localStorage.setItem('auth_token', token);
            console.log('Token stored:', token); // Debug log
        } else {
            console.error('No token received in login response');
        }
        return response.data;
    },

    logout: () => {
        localStorage.removeItem('auth_token');
        console.log('Token removed'); // Debug log
    },

    // Project endpoints
    getProjects: async () => {
        // console.log('getProjects called');
        const response = await apiClient.get<Project[]>('/projects/');
        // console.log('getProjects response:', response.data);
        return response.data;
    },

    createProject: async (project: Omit<Project, 'id'>) => {
        const response = await apiClient.post<Project>('/projects/', project);
        return response.data;
    },

    // Sutra endpoints
    getSutras: async (projectName: string) => {
        // console.log('getSutras called with projectName:', projectName);
        const response = await apiClient.get<Sutra[]>(`/sutras/?project_name=${projectName}`);
        // console.log('getSutras response:', response.data);
        // Ensure each sutra has a section and chapter value
        return response.data.map(sutra => ({
            ...sutra,
            section: sutra.section ?? 0,
            chapter: sutra.chapter ?? 0
        }));
    },

    getSutra: async (projectName: string, section: number, chapter: number, number: number) => {
        // console.log('getSutra called with:', { projectName, section, chapter, number });
        const response = await apiClient.get<Sutra>(`sutras/${projectName}/${section}/${chapter}/${number}`);
        // console.log('getSutra response:', response.data);
        return response.data;
    },

    createSutra: async (projectName: string, section: number, sutra: Omit<Sutra, 'id'>) => {
        // console.log('createSutra called with:', { projectName, section, sutra });
        const requestBody = {
            project: {
                name: projectName
            },
            sutra: {
                section: section,
                chapter: sutra.chapter,
                number: sutra.number,
                text: sutra.text
            }
        };
        const response = await apiClient.post<Sutra>(`/sutras/`, requestBody);
        // console.log('createSutra response:', response.data);
        return response.data;
    },

    updateSutra: async (projectName: string, section: number, chapter: number, number: number, sutra: Sutra) => {
        // console.log('updateSutra called with:', { projectName, section, chapter, number, sutra });
        const requestBody = {
            id: sutra.id,
            section: section,
            chapter: sutra.chapter,
            number: sutra.number,
            text: sutra.text
        };
        const response = await apiClient.put<Sutra>(`/sutras/${projectName}/${section}/${chapter}/${number}`, requestBody);
        // console.log('updateSutra response:', response.data);
        return response.data;
    },

    deleteSutra: async (projectName: string, section: number, chapter: number, number: number) => {
        // console.log('deleteSutra called with:', { projectName, section, chapter, number });
        const response = await apiClient.delete(`/sutras/${projectName}/${section}/${chapter}/${number}`);
        // console.log('deleteSutra response:', response.data);
        return response.data;
    },

    getSutraById: async (projectName: string, id: number) => {
        // console.log('getSutraById called with:', { projectName, id });
        const response = await apiClient.get<Sutra>(`/sutras_by_id/${id}`);
        // console.log('getSutraById response:', response.data);
        return response.data;
    }
}; 